import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Dirección',
    value: 'Parcela 18, Camino Vecinal, Sector El Retiro',
    sub: 'Temuco, Región de La Araucanía, Chile',
    href: 'https://maps.google.com/?q=-38.701185,-72.534385',
  },
  {
    icon: 'ri-phone-line',
    label: 'Teléfono',
    value: '+56 9 9226 5118',
    sub: 'Lunes a Jueves, 8:00 – 18:00 / Viernes 8:00 – 14:00',
    href: 'tel:+56992265118',
  },
  {
    icon: 'ri-whatsapp-line',
    label: 'WhatsApp',
    value: '+56 9 9226 5118',
    sub: 'Respuesta rápida',
    href: 'https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion',
  },
  {
    icon: 'ri-mail-line',
    label: 'Correo electrónico',
    value: 'contacto@ingemetalsur.cl',
    sub: 'Escríbenos directamente',
    href: 'mailto:contacto@ingemetalsur.cl',
  },
];

const whyContact = [
  {
    icon: 'ri-flashlight-line',
    title: 'Respuesta en 24h',
    desc: 'Nuestro equipo responde a todas las consultas en menos de un día hábil.',
  },
  {
    icon: 'ri-file-list-3-line',
    title: 'Cotización gratis',
    desc: 'Presupuesto detallado y transparente sin costo ni compromiso.',
  },
  {
    icon: 'ri-truck-line',
    title: 'Cobertura nacional',
    desc: 'Operamos en todo Chile, desde Aysén hasta el Biobío.',
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Garantía de calidad',
    desc: '+25 años de experiencia y premio SERNAGEOMIN avalan nuestro trabajo.',
  },
];

const coverage = [
  'Temuco', 'Padre Las Casas', 'Villarrica', 'Pucón',
  'Los Ángeles', 'Concepción', 'Coronel', 'Puerto Montt', 'Coyhaique',
  'Santiago', 'Valparaíso', 'La Serena', 'Antofagasta',
  'Calama', 'Iquique', 'Arica', 'Talca', 'Chillán',
  'Copiapó', 'Osorno', 'Valdivia', 'General López', 'Lautaro',
];

const reveal = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal ${r.isVisible ? 'reveal-visible' : ''}`;
const revealLeft = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-left ${r.isVisible ? 'reveal-visible' : ''}`;
const revealRight = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-right ${r.isVisible ? 'reveal-visible' : ''}`;
const revealScale = (r: ReturnType<typeof useScrollAnimation>) =>
  `reveal-scale ${r.isVisible ? 'reveal-visible' : ''}`;

const ContactoPage = () => {
  const [values, setValues] = useState({
    nombre: '',
    empresa: '',
    email: '',
    telefono: '',
    tipo_proyecto: '',
    ciudad: '',
    mensaje: '',
  });
  const [validationError, setValidationError] = useState<string | null>(null);

  const charCount = values.mensaje.length;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setValidationError(null);
    setValues((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = () => {
    setValidationError(null);

    if (!values.nombre.trim()) {
      setValidationError('Por favor ingresa tu nombre completo.');
      return;
    }
    if (!values.email.trim() || !values.email.includes('@')) {
      setValidationError('Por favor ingresa un correo electrónico válido.');
      return;
    }
    if (!values.telefono.trim()) {
      setValidationError('Por favor ingresa tu teléfono.');
      return;
    }
    if (!values.tipo_proyecto) {
      setValidationError('Por favor selecciona el tipo de proyecto.');
      return;
    }
    if (!values.mensaje.trim()) {
      setValidationError('Por favor escribe tu mensaje.');
      return;
    }
    if (values.mensaje.length > 500) {
      setValidationError('El mensaje no puede exceder 500 caracteres.');
      return;
    }

    const body = `Hola equipo de IngemetalSur,

Me interesa solicitar una cotización para un proyecto.

Datos de contacto:
- Nombre: ${values.nombre}
- Empresa: ${values.empresa || 'No indicada'}
- Correo: ${values.email}
- Teléfono: ${values.telefono}
- Tipo de proyecto: ${values.tipo_proyecto}
- Ciudad / Ubicación: ${values.ciudad || 'No indicada'}

Mensaje:
${values.mensaje}

---
Enviado desde: https://www.ingemetalsur.cl`;

    const mailto = `mailto:contacto@ingemetalsur.cl?cc=ingemetalsur@gmail.com&subject=${encodeURIComponent('Nuevo contacto desde IngemetalSur.cl')}&body=${encodeURIComponent(body)}`;

    window.open(mailto, '_blank');
    setValues({ nombre: '', empresa: '', email: '', telefono: '', tipo_proyecto: '', ciudad: '', mensaje: '' });
  };

  const heroR = useScrollAnimation();
  const cardsR = useScrollAnimation();
  const whyR = useScrollAnimation();
  const formR = useScrollAnimation();
  const infoR = useScrollAnimation();
  const ctaR = useScrollAnimation();
  const socialR = useScrollAnimation();

  return (
    <>
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=aerial%20drone%20photography%20of%20modern%20industrial%20steel%20warehouse%20construction%20site%20in%20Chile%20green%20countryside%2C%20wide%20aerial%20shot%20showing%20metal%20structure%20framework%20and%20surrounding%20landscape%2C%20dramatic%20sunset%20sky%20with%20warm%20golden%20light%20and%20deep%20blue%20tones%2C%20professional%20cinematic%20aerial%20photography%2C%20premium%20corporate%20photography%20style%2C%20no%20text%2C%20clean%20composition&width=1920&height=700&seq=contactHeroAerial002&orientation=landscape"
              alt="Contacto IngemetalSur Temuco"
              className="w-full h-full object-cover object-center opacity-90"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0C1D2E]/80 via-[#0C1D2E]/60 to-[#0C1D2E]/30"></div>
          </div>
          <div ref={heroR.ref} className={`relative z-10 max-w-7xl mx-auto px-4 md:px-8 ${reveal(heroR)}`}>
            <div className="max-w-2xl">
              <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Estamos en Temuco</span>
              <h1 className="text-4xl md:text-5xl font-black text-white mt-2 mb-4 leading-tight">
                Hablemos de tu<br />
                <span className="text-[#C88A2A]">próximo proyecto</span>
              </h1>
              <p className="text-slate-200 text-lg leading-relaxed mb-6">
                Cotización sin costo ni compromiso. Nuestro equipo te responde en menos de 24 horas.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  WhatsApp
                </a>
                <a
                  href="tel:+56992265118"
                  className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 hover:scale-[1.02] active:scale-[0.98] border border-white/30 text-white font-bold px-7 py-3.5 rounded-md transition-all duration-200 whitespace-nowrap"
                >
                  <i className="ri-phone-line text-lg"></i>
                  +56 9 9226 5118
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Cards - Realzadas */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={cardsR.ref} className={`text-center mb-14 ${reveal(cardsR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Conecta con nosotros</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                Comunícate directamente
              </h2>
              <p className="text-[#475569] text-base max-w-lg mx-auto mt-3">
                Elige el canal que prefieras. Te respondemos por WhatsApp, correo o teléfono en menos de 24 horas.
              </p>
            </div>
            <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 ${reveal(cardsR)}`}>
              {contactInfo.map((item, i) => (
                <a
                  key={item.label}
                  href={item.href}
                  target={item.href.startsWith('http') ? '_blank' : undefined}
                  rel={item.href.startsWith('http') ? 'nofollow noopener noreferrer' : undefined}
                  className={`bg-white rounded-xl p-8 flex flex-col items-start gap-4 hover:border-[#C88A2A] border border-transparent hover:shadow-xl transition-all duration-300 cursor-pointer group hover:-translate-y-2 reveal ${cardsR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-[#C88A2A]/10 rounded-xl group-hover:bg-[#C88A2A] transition-colors duration-300">
                    <i className={`${item.icon} text-[#C88A2A] group-hover:text-white text-2xl transition-colors duration-300`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#64748B] uppercase tracking-wider mb-1.5">{item.label}</p>
                    <p className="text-[#0C1D2E] font-bold text-base">{item.value}</p>
                    <p className="text-[#64748B] text-sm mt-1 leading-relaxed">{item.sub}</p>
                  </div>
                  <div className="mt-auto pt-2 flex items-center gap-1 text-[#C88A2A] text-xs font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span>Contactar ahora</span>
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300"></i>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Why Contact Us */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div ref={whyR.ref} className={`text-center mb-14 ${reveal(whyR)}`}>
              <span className="text-[#C88A2A] text-xs font-bold uppercase tracking-[0.2em]">Confianza</span>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#0C1D2E] mt-3">
                ¿Por qué contactarnos?
              </h2>
              <p className="text-[#475569] text-base max-w-lg mx-auto mt-3">
                Más de 25 años construyendo infraestructura industrial con estándares de calidad certificados.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyContact.map((item, i) => (
                <div
                  key={item.title}
                  className={`bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-7 text-center hover:border-[#C88A2A]/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 reveal ${whyR.isVisible ? 'reveal-visible' : ''}`}
                  style={{ transitionDelay: `${i * 120}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-[#0C1D2E] rounded-lg mx-auto mb-5 hover:scale-110 hover:bg-[#C88A2A] transition-all duration-300">
                    <i className={`${item.icon} text-white text-2xl`}></i>
                  </div>
                  <h3 className="font-heading font-bold text-[#0C1D2E] text-base mb-2">{item.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Form + Map */}
        <section className="py-24 bg-[#0C1D2E]">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-start">
              {/* Form */}
              <div ref={formR.ref} className={`${revealLeft(formR)}`}>
                <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Formulario de contacto</span>
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-2 mb-2">Envíanos un mensaje</h2>
                <p className="text-white/50 text-sm mb-10">Completa el formulario y te contactamos a la brevedad.</p>

                <div className="space-y-5">
                  {validationError && (
                    <div className="bg-red-500/10 border border-red-500/30 rounded-md p-4 flex items-start gap-3">
                      <div className="w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                        <i className="ri-error-warning-line text-red-400 text-lg"></i>
                      </div>
                      <p className="text-red-300 text-sm font-semibold">{validationError}</p>
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                        Nombre completo <span className="text-[#C88A2A]">*</span>
                      </label>
                      <input
                        type="text"
                        name="nombre"
                        value={values.nombre}
                        onChange={handleChange}
                        placeholder="Ej: Juan Pérez"
                        className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                        Empresa (opcional)
                      </label>
                      <input
                        type="text"
                        name="empresa"
                        value={values.empresa}
                        onChange={handleChange}
                        placeholder="Ej: Agrícola del Sur"
                        className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                        Correo electrónico <span className="text-[#C88A2A]">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        placeholder="correo@ejemplo.cl"
                        className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                        Teléfono <span className="text-[#C88A2A]">*</span>
                      </label>
                      <input
                        type="tel"
                        name="telefono"
                        value={values.telefono}
                        onChange={handleChange}
                        placeholder="+56 9 XXXX XXXX"
                        className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                      Tipo de proyecto <span className="text-[#C88A2A]">*</span>
                    </label>
                    <select
                      name="tipo_proyecto"
                      value={values.tipo_proyecto}
                      onChange={handleChange}
                      className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors cursor-pointer appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23ffffff'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '16px' }}
                    >
                      <option value="" className="bg-[#0C1D2E] text-white">Selecciona una opción</option>
                      <option value="Galpón Industrial" className="bg-[#0C1D2E] text-white">Galpón Industrial</option>
                      <option value="Local Comercial" className="bg-[#0C1D2E] text-white">Local Comercial</option>
                      <option value="Obra Civil" className="bg-[#0C1D2E] text-white">Obra Civil</option>
                      <option value="Proyecto Estructural" className="bg-[#0C1D2E] text-white">Proyecto Estructural</option>
                      <option value="Piscicultura" className="bg-[#0C1D2E] text-white">Piscicultura</option>
                      <option value="Fabricación de Estructuras Metálicas" className="bg-[#0C1D2E] text-white">Fabricación de Estructuras Metálicas</option>
                      <option value="Otro" className="bg-[#0C1D2E] text-white">Otro</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                      Ciudad / Ubicación del proyecto
                    </label>
                    <input
                      type="text"
                      name="ciudad"
                      value={values.ciudad}
                      onChange={handleChange}
                      placeholder="Ej: Temuco, Villarrica, Puerto Montt..."
                      className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-white/50 uppercase tracking-wider mb-1.5">
                      Mensaje <span className="text-[#C88A2A]">*</span>
                    </label>
                    <textarea
                      name="mensaje"
                      value={values.mensaje}
                      onChange={handleChange}
                      rows={4}
                      maxLength={500}
                      placeholder="Cuéntanos sobre tu proyecto: dimensiones aproximadas, uso, plazo estimado..."
                      className="w-full border border-white/10 bg-white/5 rounded-md px-4 py-3 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#C88A2A] focus:bg-white/10 transition-colors resize-none"
                    />
                    <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-white/40'}`}>
                      {charCount}/500 caracteres
                    </p>
                  </div>

                  <button
                    onClick={handleSubmit}
                    className="w-full bg-[#C88A2A] hover:bg-[#A06B1A] hover:scale-[1.02] active:scale-[0.98] text-white font-bold py-3.5 rounded-md transition-all duration-200 text-sm whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
                  >
                    <i className="ri-send-plane-line"></i>
                    Enviar mensaje
                  </button>

                  <p className="text-white/40 text-xs text-center">
                    Se abrirá tu correo con el mensaje preparado. Si prefieres respuesta inmediata,{' '}
                    <a
                      href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion"
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="text-[#C88A2A] font-semibold hover:underline"
                    >
                      escríbenos por WhatsApp
                    </a>
                  </p>
                </div>
              </div>

              {/* Map + Info */}
              <div ref={infoR.ref} className={`flex flex-col gap-6 ${revealRight(infoR)}`}>
                <div>
                  <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Ubicación</span>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mt-2 mb-2">Encuéntranos en Temuco</h2>
                  <p className="text-white/50 text-sm mb-6">Parcela 18, Camino Vecinal, Sector El Retiro, Temuco, Araucanía, Chile.</p>
                </div>

                {/* Google Maps Embed */}
                <div className="w-full h-72 rounded-lg overflow-hidden border border-white/10 hover:border-white/20 transition-colors duration-300">
                <iframe
                    title="Ubicación IngemetalSur Temuco"
                    src="https://www.google.com/maps?q=-38.701185,-72.534385&output=embed&z=16"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>

                {/* Hours */}
                <div className="bg-white/5 rounded-lg p-6 border border-white/10 hover:border-white/20 transition-all duration-300">
                  <h3 className="font-bold text-white text-base mb-4 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-time-line text-[#C88A2A]"></i>
                    </div>
                    Horario de atención
                  </h3>
                  <ul className="space-y-2">
                    {[
                      { day: 'Lunes – Jueves', hours: '08:00 – 18:00' },
                      { day: 'Viernes', hours: '08:00 – 14:00' },
                      { day: 'Sábado', hours: 'Cerrado' },
                      { day: 'Domingo', hours: 'Cerrado' },
                    ].map((h) => (
                      <li key={h.day} className="flex items-center justify-between text-sm">
                        <span className="text-white/50">{h.day}</span>
                        <span className={`font-semibold ${h.hours === 'Cerrado' ? 'text-red-400' : 'text-white'}`}>{h.hours}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-white/40 text-xs mt-4">
                    WhatsApp disponible fuera de horario para consultas urgentes.
                  </p>
                </div>

                {/* Coverage */}
                <div className="bg-[#C88A2A]/10 rounded-lg p-6 border border-[#C88A2A]/20">
                  <h3 className="font-bold text-white text-base mb-3 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-map-2-line text-[#C88A2A]"></i>
                    </div>
                    Cobertura geográfica
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {coverage.map((city, i) => (
                      <span
                        key={city}
                        className={`bg-white/10 border border-[#C88A2A]/20 text-white/70 text-xs font-semibold px-3 py-1 rounded-full hover:bg-[#C88A2A]/20 hover:text-white transition-all duration-200 cursor-default reveal ${infoR.isVisible ? 'reveal-visible' : ''}`}
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        {city}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA WhatsApp */}
        <section className="py-20 bg-[#F8FAFC]">
          <div ref={ctaR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${revealScale(ctaR)}`}>
            <div className="w-16 h-16 flex items-center justify-center bg-[#25D366]/10 rounded-full mx-auto mb-5 hover:scale-110 transition-transform duration-300">
              <i className="ri-whatsapp-line text-[#25D366] text-3xl"></i>
            </div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-[#0C1D2E] mb-3">
              ¿Prefieres hablar directamente?
            </h2>
            <p className="text-[#475569] text-base mb-8 max-w-md mx-auto">
              Escríbenos por WhatsApp y recibe atención personalizada de nuestro equipo en Temuco.
            </p>
            <a
              href="https://wa.me/56992265118?text=Hola%2C%20me%20interesa%20solicitar%20una%20cotizacion"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1da851] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-10 py-4 rounded-md transition-all duration-200 whitespace-nowrap text-base"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Chatear por WhatsApp
            </a>
          </div>
        </section>

        {/* Social Media */}
        <section className="py-16 bg-white">
          <div ref={socialR.ref} className={`max-w-3xl mx-auto px-4 md:px-8 text-center ${reveal(socialR)}`}>
            <span className="text-[#C88A2A] text-sm font-bold uppercase tracking-widest">Síguenos</span>
            <h2 className="text-2xl md:text-3xl font-black text-[#0C1D2E] mt-2 mb-3">
              Conecta con nosotros
            </h2>
            <p className="text-[#475569] text-base mb-8">
              Sigue nuestras obras y proyectos en redes sociales. Compartimos fotos, videos y novedades de IngemetalSur.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.instagram.com/ingemetalsurtco"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#0C1D2E] hover:bg-[#1A3A5C] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap text-base border border-[#C88A2A]/30"
              >
                <i className="ri-instagram-line text-xl"></i>
                Instagram
              </a>
              <a
                href="https://www.facebook.com/ingemetalsur"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="inline-flex items-center justify-center gap-3 bg-[#1877F2] hover:bg-[#166fe5] hover:scale-[1.02] active:scale-[0.98] text-white font-bold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap text-base"
              >
                <i className="ri-facebook-circle-line text-xl"></i>
                Facebook
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </>
  );
};

export default ContactoPage;